/**
 * @file useListings.js
 * @brief Loads the public listing feed with filters, pagination, and protected preview images.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export default function useListings({
    api,
    accessToken,
    page,
    search,
    locationFilter,
    categoryFilter,
    showFavorites = false,
}) {
    const [listings, setListings] = useState([]);
    const [imagesMap, setImagesMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [count, setCount] = useState(0);
    const [next, setNext] = useState(null);
    const [previous, setPrevious] = useState(null);

    const latestRequestIdRef = useRef(0);
    const objectUrlsRef = useRef([]);

    const revokeObjectUrls = useCallback(() => {
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current = [];
    }, []);

    const fetchListings = useCallback(
        async (pageNumber = 1) => {

            const requestId = latestRequestIdRef.current + 1;
            latestRequestIdRef.current = requestId;

            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams({ status: "active", page: pageNumber });
                if (search) params.append("search", search);
                if (locationFilter) params.append("location", locationFilter.value);
                if (categoryFilter) params.append("category", categoryFilter.value);
                if (showFavorites) params.append("favorites", "true");

                const res = await api.get(`listings/?${params.toString()}`);
                const data = res.data;

                const map = {};
                const newObjectUrls = [];

                await Promise.all(
                    data.results.map(async (listing) => {
                        if (listing.images && listing.images.length > 0) {
                            const headers = {};
                            if (accessToken) {
                                headers["Authorization"] = `Bearer ${accessToken}`;
                            }
                            const imgRes = await fetch(listing.images[0].url, { headers });
                            if (!imgRes.ok) {
                                map[listing.id] = null;
                                return;
                            }
                            const blob = await imgRes.blob();
                            const objectUrl = URL.createObjectURL(blob);
                            map[listing.id] = objectUrl;
                            newObjectUrls.push(objectUrl);
                        } else {
                            map[listing.id] = null;
                        }
                    })
                );

                if (requestId !== latestRequestIdRef.current) {
                    newObjectUrls.forEach((url) => URL.revokeObjectURL(url));
                    return;
                }

                setListings(data.results);
                setCount(data.count);
                setNext(data.next);
                setPrevious(data.previous);

                revokeObjectUrls();
                objectUrlsRef.current = newObjectUrls;
                setImagesMap(map);
            } catch (err) {
                if (requestId !== latestRequestIdRef.current) return;
                setError(err);
            } finally {
                if (requestId === latestRequestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [api, accessToken, search, locationFilter, categoryFilter, showFavorites, revokeObjectUrls]
    );

    useEffect(() => {
        fetchListings(page);
    }, [page, fetchListings]);

    useEffect(() => () => revokeObjectUrls(), [revokeObjectUrls]);

    return {
        listings,
        setListings,
        imagesMap,
        loading,
        error,
        count,
        next,
        previous,
        fetchListings,
    };
}
